import {Text} from '@mantine/core';
import React from 'react';

import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {JSONValue} from 'util/Types';

import {maybeGetValueFromConfig} from '../../step-config-settings/StepConfigSettingsUtils';
import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {ConfigShapePath, CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import ColumnMappingGrid, {ColumnMappingRow} from './ColumnMappingGrid';

function titleCase(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase());
}

function inputDataNameForSpec(spec: PipelineConfigJsonSpec): string {
  if (spec.kind === 'columnReference') {
    return spec.inputDataName;
  }
  if (spec.kind === 'array' && spec.elementSpec.kind === 'columnReference') {
    return spec.elementSpec.inputDataName;
  }
  return '';
}

function rowsForSubfield(
  mappingPath: ConfigShapePath,
  subfieldName: string,
  spec: PipelineConfigJsonSpec
): Array<ColumnMappingRow> {
  if (spec.kind === 'columnReference') {
    return [
      {
        id: subfieldName,
        displayName: titleCase(subfieldName),
        targetPath: [...mappingPath, subfieldName],
        required: spec.required,
        multiSelect: false,
      },
    ];
  }
  if (spec.kind === 'array' && spec.elementSpec.kind === 'columnReference') {
    return [
      {
        id: subfieldName,
        displayName: titleCase(subfieldName),
        targetPath: [...mappingPath, subfieldName],
        required: spec.required,
        multiSelect: true,
      },
    ];
  }
  if (spec.kind === 'schemaFields') {
    const resolved = spec.__resolvedRows ?? [];
    return resolved.map((row) => ({
      id: `${subfieldName}.${row.systemName}`,
      displayName: row.displayName,
      targetPath: [...mappingPath, subfieldName, row.systemName],
      required: row.required ?? false,
      multiSelect: false,
      fieldType: row.fieldType,
    }));
  }
  return [];
}

export default function ColumnMappingEditor({
  config,
  configShape,
  configShapePath,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps & {configShapePath: ConfigShapePath}): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();

  if (configShape.kind !== 'columnMapping') {
    return <></>;
  }

  const schemaInaccessible = Object.values(configShape.fields).some(
    (spec) => spec.kind === 'schemaFields' && spec.__schemaInaccessible === true
  );
  if (schemaInaccessible) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="You don't have permission to view this schema's fields, so its column mapping can't be shown or edited."
      />
    );
  }

  const rows = Object.entries(configShape.fields).flatMap(([subfieldName, spec]) =>
    rowsForSubfield(configShapePath, subfieldName, spec)
  );

  const inputDataName =
    Object.values(configShape.fields)
      .map((spec) => inputDataNameForSpec(spec))
      .find((name) => name !== '') ?? '';
  const {dataFrameSpec} = getDataFrameInfoOrEmptyInfo(dataFramesInfos, inputDataName);

  if (rows.length === 0) {
    return (
      <Text color="dimmed" size="sm">
        Select a schema to map its fields to dataset columns.
      </Text>
    );
  }

  return (
    <ColumnMappingGrid
      rows={rows}
      dataFrameSpec={dataFrameSpec}
      valueForPath={(path: ConfigShapePath) => maybeGetValueFromConfig(config, path)}
      onChange={(path, value: JSONValue) => {
        onChangeConfig(config, path, value);
      }}
      disabled={disabled}
    />
  );
}
